import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { syncExistingSubscription } from '../../utils/pushNotifications';

function isStandalone() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch {
    return false;
  }
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
}

// Builds a manifest for THIS academy (name + logo + brand color) and swaps
// it in via a Blob URL — the standard technique for a multi-tenant PWA on a
// single shared domain, since browsers request /manifest.json directly and
// don't know which academy a given visitor belongs to. Reverts to the
// static default manifest on unmount, i.e. as soon as the user leaves the
// Academy Portal.
function useAcademyManifest(academy) {
  useEffect(() => {
    if (!academy) return undefined;
    const icon = academy.branding?.logoUrl;

    let manifestLink = document.querySelector('link[rel="manifest"]');
    const previousManifestHref = manifestLink ? manifestLink.getAttribute('href') : null;
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.setAttribute('rel', 'manifest');
      document.head.appendChild(manifestLink);
    }

    const manifest = {
      short_name: academy.name || 'Academy Portal',
      name: `${academy.name || 'Academy'} Portal`,
      icons: [
        { src: icon || `${window.location.origin}/logo192.png`, type: 'image/png', sizes: '192x192' },
        { src: icon || `${window.location.origin}/logo512.png`, type: 'image/png', sizes: '512x512' },
      ],
      start_url: '/academy/home',
      display: 'standalone',
      theme_color: academy.branding?.primaryColor || '#1a3d3a',
      background_color: '#f8fafc',
    };

    const blobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/json' }));
    manifestLink.setAttribute('href', blobUrl);

    // iOS reads apple-touch-icon directly, not the manifest, for its
    // home-screen icon — swap that too so "Add to Home Screen" matches.
    let appleIconLink = document.querySelector('link[rel="apple-touch-icon"]');
    const previousAppleIconHref = appleIconLink ? appleIconLink.getAttribute('href') : null;
    if (icon && appleIconLink) appleIconLink.setAttribute('href', icon);

    return () => {
      URL.revokeObjectURL(blobUrl);
      if (previousManifestHref) manifestLink.setAttribute('href', previousManifestHref);
      if (icon && appleIconLink && previousAppleIconHref) appleIconLink.setAttribute('href', previousAppleIconHref);
    };
  }, [academy]);
}

export default function PwaInstallPrompt({ academy }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  useAcademyManifest(academy);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [variant, setVariant] = useState(null); // 'native' | 'ios'
  const [visible, setVisible] = useState(false);

  // Registered here (not app-wide) so only the Academy Portal ever gets a
  // service worker — CRM/Onboarding/Super stay plain, uncached pages.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* non-blocking */ });
    }
  }, []);

  // Re-link this device's push subscription to whoever just signed in, if
  // they turned notifications on here before (sign-out unlinks it — see
  // firebase/auth.js). No-op otherwise; never prompts.
  useEffect(() => {
    if (user?.uid) syncExistingSubscription(user.uid).catch(() => { /* non-blocking */ });
  }, [user?.uid]);

  useEffect(() => {
    if (isStandalone() || !user?.uid) return undefined;

    let dismissed = null;
    try { dismissed = localStorage.getItem(`pwaInstallDismissed:${user.uid}`); } catch { /* ignore */ }
    if (dismissed) return undefined;

    if (isIOS()) {
      setVariant('ios');
      setVisible(true);
      return undefined;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVariant('native');
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [user?.uid]);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(`pwaInstallDismissed:${user?.uid}`, '1'); } catch { /* ignore */ }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 z-50 px-4 flex justify-center" style={{ bottom: 76 }}>
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl shadow-lg p-4 flex items-start gap-3">
        {academy?.branding?.logoUrl ? (
          <img src={academy.branding.logoUrl} alt="" className="h-10 w-10 rounded-lg object-contain bg-gray-50 flex-shrink-0" />
        ) : (
          <div className="h-10 w-10 rounded-lg bg-brand-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
            {academy?.name?.[0]?.toUpperCase() || 'A'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {academy?.name
              ? t('academy:pwaInstallPrompt.installOnDeviceWithName', 'Install {{name}} on your device', { name: academy.name })
              : t('academy:pwaInstallPrompt.installOnDeviceGeneric', 'Install on your device')}
          </p>
          {variant === 'ios' ? (
            <p className="text-xs text-gray-500 mt-0.5">
              <Trans
                i18nKey="academy:pwaInstallPrompt.iosInstructions"
                t={t}
                components={{ b1: <span className="font-semibold" />, b2: <span className="font-semibold" /> }}
              >
                Tap <span className="font-semibold">Share</span>, then <span className="font-semibold">Add to Home Screen</span> — one tap to get back in, no app store needed.
              </Trans>
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">{t('academy:pwaInstallPrompt.genericInstructions', 'Add it to your home screen for one-tap access — no app store needed.')}</p>
          )}
          <div className="flex gap-2 mt-2.5">
            {variant === 'native' && (
              <button
                type="button"
                onClick={handleInstall}
                className="text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg px-3 py-1.5 transition-colors"
              >
                {t('academy:pwaInstallPrompt.install', 'Install')}
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="text-xs font-medium text-gray-500 hover:text-gray-700 px-3 py-1.5 transition-colors"
            >
              {variant === 'ios' ? t('academy:pwaInstallPrompt.gotIt', 'Got it') : t('academy:pwaInstallPrompt.notNow', 'Not now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
